import { AlertCircle } from "lucide-react";
import type { ComponentProps } from "react";

import {
  useGetEncounterById,
  useGetPatientById,
} from "@elation/api-client/src/stitch-web";

import { AgendaV2, type GapListUploadV2 } from "@src/features/agenda";
import { PatientEntity } from "@src/features/patients";
import { VisitTour } from "@src/features/tourguide";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Skeleton } from "@src/shared/components/ui/skeleton";

import { EncounterEntity } from "../../../../domain";
import {
  CompletedVisit,
  type PatientSelectorPatientSummaryV2,
  PatientSelectorV2,
  VisitGeneratingNote,
  VisitRecordingBanner,
  type VisitRecordingV2,
  VisitStartControls,
  VisitTranscript,
} from "../../../components/v2";

export type VisitPatientSummaryV2 = PatientSelectorPatientSummaryV2;
export type { GapListUploadV2 };

export type {
  CompletedVisitMedicalCodeV2,
  VisitRecordingV2,
  VisitTranscriptItemV2,
} from "../../../components/v2";

export interface VisitPageV2Props {
  encounterId?: number;
  patientId?: number;
  patient?: VisitPatientSummaryV2 | null;
  recording?: VisitRecordingV2;
  scribingSessionId?: string | null;
  defaultTranscriptOpen?: boolean;
  isGeneratingNote?: boolean;
  isPatientSelectorOpen?: boolean;
  isLoadingEncounter?: boolean;
  isEncounterError?: boolean;
  encounterError?: unknown;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onPatientSelect?: ComponentProps<typeof PatientSelectorV2>["onPatientSelect"];
  onStartVisit?: () => void;
}

function EncounterLoadingState() {
  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background">
      <HorizontalFlexbox
        alignItems="center"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <Skeleton className="size-8 rounded-full" />
        <VerticalFlexbox className="gap-1">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-28" />
        </VerticalFlexbox>
      </HorizontalFlexbox>
      <VerticalFlexbox className="px-[18px] py-4">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="mt-2 h-3 w-48" />
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

function EncounterErrorState({ error }: { error?: unknown }) {
  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background p-[18px]">
      <Alert variant="destructive">
        <AlertCircle className="size-4" aria-hidden="true" />
        <AlertTitle>Unable to load visit</AlertTitle>
        <ErrorMessage
          as={AlertDescription}
          error={error ?? true}
          fallback="Something went wrong retrieving this visit."
        />
      </Alert>
    </VerticalFlexbox>
  );
}

interface VisitPageV2CreateEncounterProps {
  patientId?: number;
  patient?: VisitPatientSummaryV2 | null;
  isPatientSelectorOpen?: boolean;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onPatientSelect?: ComponentProps<typeof PatientSelectorV2>["onPatientSelect"];
}

function VisitPageV2CreateEncounter({
  patientId,
  patient,
  isPatientSelectorOpen = false,
  onPatientSelectorOpenChange,
  onPatientSelect,
}: VisitPageV2CreateEncounterProps) {
  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background">
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <VisitTour.CreatePatientStep>
          <PatientSelectorV2
            patient={patient}
            open={isPatientSelectorOpen}
            canChangePatient
            onOpenChange={onPatientSelectorOpenChange}
            onPatientSelect={onPatientSelect}
          />
        </VisitTour.CreatePatientStep>
      </HorizontalFlexbox>

      <AgendaV2
        className="min-h-[460px]"
        encounterId={undefined}
        patientId={patientId}
      />
    </VerticalFlexbox>
  );
}

interface VisitPageV2WithEncounterProps {
  encounterId: number;
  patientId?: number;
  recording?: VisitRecordingV2;
  scribingSessionId?: string | null;
  defaultTranscriptOpen?: boolean;
  isGeneratingNote?: boolean;
  isPatientSelectorOpen?: boolean;
  isLoadingEncounter?: boolean;
  isEncounterError?: boolean;
  encounterError?: unknown;
  onPatientSelectorOpenChange: (open: boolean) => void;
  onStartVisit?: () => void;
}

function VisitPageV2WithEncounter({
  encounterId,
  patientId,
  recording,
  scribingSessionId,
  defaultTranscriptOpen = false,
  isGeneratingNote = false,
  isPatientSelectorOpen = false,
  isLoadingEncounter = false,
  isEncounterError = false,
  encounterError = null,
  onPatientSelectorOpenChange,
  onStartVisit,
}: VisitPageV2WithEncounterProps) {
  const encounter = useGetEncounterById(encounterId);
  const encounterData = encounter.data?.data;
  const encounterPatient = useGetPatientById(patientId ?? 0, {
    query: {
      enabled: patientId != null,
    },
  });
  const isLoadingPatient = patientId != null && encounterPatient.isLoading;
  const isPatientError = patientId != null && encounterPatient.isError;
  const fetchedPatient = encounterPatient.data?.data;
  const patientSummary = fetchedPatient
    ? {
        name: PatientEntity.displayName(fetchedPatient) ?? "Unknown patient",
        initials: PatientEntity.initials(fetchedPatient) ?? "?",
        metadata: PatientEntity.listMetadata(fetchedPatient),
      }
    : null;
  const activeRecording = patientSummary ? recording : undefined;

  if (isLoadingEncounter || isLoadingPatient) {
    return <EncounterLoadingState />;
  }

  if (isEncounterError) {
    return <EncounterErrorState error={encounterError} />;
  }

  if (isPatientError) {
    return <EncounterErrorState error={encounterPatient.error} />;
  }

  if (
    EncounterEntity.isCompleted(encounterData) &&
    EncounterEntity.hasGeneratedNote(encounterData)
  ) {
    return (
      <CompletedVisit
        patient={patientSummary}
        noteText={encounterData?.note}
        encounterId={encounterId}
      />
    );
  }

  if (isGeneratingNote || EncounterEntity.isGeneratingNote(encounterData)) {
    return <VisitGeneratingNote />;
  }

  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background">
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <PatientSelectorV2
          patient={patientSummary}
          open={isPatientSelectorOpen}
          canChangePatient={false}
          onOpenChange={onPatientSelectorOpenChange}
        />

        <VisitStartControls
          encounter={encounterData}
          encounterId={encounterId}
          patient={patientSummary}
          recording={recording}
          onStartVisit={onStartVisit}
        />
      </HorizontalFlexbox>

      {activeRecording ? (
        <div className="mx-[18px] mt-2">
          <VisitRecordingBanner recording={activeRecording} />
        </div>
      ) : null}

      <AgendaV2
        className="min-h-[460px]"
        encounterId={encounterId}
        patientId={patientId}
      />

      {activeRecording ? (
        <div className="px-[18px]">
          <VisitTranscript
            defaultOpen={defaultTranscriptOpen}
            encounterId={encounterId}
            scribingSessionId={scribingSessionId}
          />
        </div>
      ) : null}
    </VerticalFlexbox>
  );
}

export function VisitPageV2({
  encounterId,
  patientId,
  patient,
  recording,
  scribingSessionId,
  defaultTranscriptOpen = false,
  isGeneratingNote = false,
  isPatientSelectorOpen = false,
  isLoadingEncounter = false,
  isEncounterError = false,
  encounterError = null,
  onPatientSelectorOpenChange,
  onPatientSelect,
  onStartVisit,
}: VisitPageV2Props) {
  if (encounterId == null) {
    return (
      <VisitPageV2CreateEncounter
        patientId={patientId}
        patient={patient}
        isPatientSelectorOpen={isPatientSelectorOpen}
        onPatientSelectorOpenChange={onPatientSelectorOpenChange}
        onPatientSelect={onPatientSelect}
      />
    );
  }

  return (
    <VisitPageV2WithEncounter
      encounterId={encounterId}
      patientId={patientId}
      recording={recording}
      scribingSessionId={scribingSessionId}
      defaultTranscriptOpen={defaultTranscriptOpen}
      isGeneratingNote={isGeneratingNote}
      isPatientSelectorOpen={isPatientSelectorOpen}
      isLoadingEncounter={isLoadingEncounter}
      isEncounterError={isEncounterError}
      encounterError={encounterError}
      onPatientSelectorOpenChange={onPatientSelectorOpenChange}
      onStartVisit={onStartVisit}
    />
  );
}
